/**
 * What a call IS, as data: the phases it moves through, who is on it, and every
 * fact the window draws from.
 *
 * Lifted out of `CallProvider.tsx` unchanged, which had grown past the point
 * where the machine that DRIVES a call and the description of what a call is
 * could be read in one sitting (AGENTS.md §4). Nothing here imports React on
 * purpose: this is the vocabulary, and the provider is the only thing that
 * writes it.
 *
 * `CallProvider` re-exports all of it, so no caller had to change its import.
 */
import type {
  CallSource,
  CallTransferState,
  NoticeLocale,
  PhoneMatch,
  RecordingStopBlock,
  TransferBlock,
} from "../../lib/calls";
import type { CallLink } from "./linkQuality";

export type CallPhase =
  | "idle"
  | "incoming"
  | "preparing"
  | "connecting"
  | "ringing"
  | "active"
  | "ended"
  | "error";

export type CallDirection = "outbound" | "inbound";

/**
 * The phases in which a call would be DESTROYED by the page going away.
 *
 * `incoming` is deliberately not on this list. An offer nobody has accepted is
 * not a conversation: closing the tab on a ringing phone drops the caller into
 * voicemail, which is exactly where declining it puts them, so a browser prompt
 * there would be asking the operator to confirm the thing they were already
 * allowed to do with one click. `ended` and `error` are off it for the opposite
 * reason - the call is over and only the after-call screen would be lost.
 *
 * One list, exported, because two readers already want it and a second copy is
 * how the window and the guard drift apart: `CallWindow` uses it to decide
 * whether to draw the live surface, and `useUnloadWarning` to decide whether
 * closing the page needs a question.
 */
export const LIVE_PHASES: readonly CallPhase[] = ["preparing", "connecting", "ringing", "active"];

/** Is a call up right now, in the sense that hanging up is the only way out? */
export function isLiveCall(phase: CallPhase): boolean {
  return LIVE_PHASES.includes(phase);
}

/**
 * May a new call start in this window now?
 *
 * Nothing up, or only the screen of a call that is over. Shared by the provider
 * and the AI dock, which greys its "in this browser" takeover out on the same
 * rule the provider will apply to the press.
 */
export function canStartCall(phase: CallPhase): boolean {
  return phase === "idle" || phase === "ended" || phase === "error";
}

export interface CallTarget {
  phone: string;
  companyName?: string;
  regCode?: string;
  leadDomain?: string;
  /**
   * The CRM record the click came from. Filled in wherever the number was
   * printed on a client, a lead or one of their contacts, so the call is filed
   * against that record from the moment it is placed.
   */
  clientId?: string;
  leadId?: string;
  /**
   * The PERSON whose own number was clicked, when the click happened on a
   * contact row rather than on a company one.
   *
   * Travels instead of `clientId`: the server takes the company from the
   * contact itself, so one row can never name a person at one company and the
   * company of another.
   */
  contactId?: string;
  source: CallSource;
  /**
   * Which of our own lines to show the far end. Only the dial pad offers the
   * choice; the server falls back to the account caller id without it, and
   * refuses a number this account may not present.
   */
  fromNumber?: string;
  /**
   * What this one call should be placed with. Only the dial pad fills these in;
   * everywhere else the number is clicked without a chance to choose, and an
   * omitted flag means the account setting.
   *
   * They ride on the target rather than on a separate argument so a redial
   * repeats the same choice instead of quietly reverting to the default.
   */
  record?: boolean;
  transcribe?: boolean;
  notice?: boolean;
  /** The announcement language. Omitted is the account's own base wording. */
  noticeLocale?: NoticeLocale;
  /**
   * Take this call on the operator's own mobile rather than in this browser.
   *
   * Unlike the four above it changes what the BROWSER does, not only what the
   * call does: no Device is acquired, no microphone is opened and no `Call`
   * object ever exists, because the server has already rung the operator's
   * phone by the time `POST /calls` answers.
   */
  viaMobile?: boolean;
}

/** Who is on the line, as far as anything is known while it is still ringing. */
export interface IncomingCall {
  /** The CallLog row the webhook already created, from the TwiML parameters. */
  callLogId: string;
  /** The caller's number, or "" when they withheld it. */
  from: string;
  /**
   * Everyone this number could be. A list rather than a name because a shared
   * switchboard is normal - see `lib/lookup.ts`. Empty until the lookup lands,
   * and it is allowed to stay empty.
   */
  matches: PhoneMatch[];
  /**
   * What the caller said before anybody was rung, when the menu asked them.
   *
   * Both arrive as `<Client>` parameters on the offer itself, which is the only
   * channel that reaches this card in time: everything else the call window
   * knows about the row is fetched AFTER the operator has answered. Empty means
   * the question was not asked or the caller said nothing - the same value for
   * both, because neither is a choice.
   */
  intentDepartment: string;
  /** The announcement language, as a base tag ("ru"). Empty is the base wording. */
  locale: string;
  /**
   * The offer's own leg sid, when the row id did NOT come with it - which is a
   * PARKED caller: the operator is rung into a room by the REST API, and that
   * offer has no `<Parameter>` to carry the id. The row is found by this sid,
   * and the claim sends it, because on that bridge the claim is what decides
   * which operator gets the caller. "" on the `<Dial>` path.
   */
  legSid: string;
}

export interface CallState {
  phase: CallPhase;
  direction: CallDirection;
  target: CallTarget | null;
  /** The offered call, while one is ringing and after it is answered. */
  incoming: IncomingCall | null;
  /** Normalized number actually dialed. */
  dialedNumber: string | null;
  /**
   * The CallLog row this attempt writes to, known from `preparing` onwards.
   *
   * The live window needs it to save the operator's note while the call is
   * still up: before this was exposed, the note box was local state and the
   * text went out with the window.
   */
  callLogId: string | null;
  /** Machine error code for the modal's message picker. */
  error: string | null;
  muted: boolean;
  /** Recording on for this call (from the token response). */
  recording: boolean;
  /**
   * Why the recording cannot be stopped by hand, or "" when it can.
   *
   * Server-decided and polled, like `transferBlocked`. It is what puts the stop
   * button beside the red dot - and, one poll after a stop lands, what takes it
   * away again without the window needing to know that a stop writes four
   * columns on the row.
   */
  recordingStopBlocked: RecordingStopBlock;
  /** A stop request is in flight. Like hold, this one is a network round trip. */
  recordingStopPending: boolean;
  /**
   * The stop was made, and whether Twilio's copy of the partial audio went with
   * it. Null until somebody presses the button.
   *
   * Kept as its own field rather than inferred from `recording` going false,
   * because the two say different things: the dot going out is what the far end
   * was promised, and this is whether it was fully delivered. A stop whose
   * delete failed leaves a file on the Twilio account, and the operator who made
   * the promise is the person who should be told.
   */
  recordingStopped: { destroyed: boolean } | null;
  /** The last stop attempt's own refusal, shown beside the control. */
  recordingStopError: string | null;
  /** Epoch ms when the callee answered - drives the live timer. */
  answeredAt: number | null;
  /** Seconds talked, frozen at hangup for the ended screen. */
  finalDuration: number | null;
  /**
   * This call is bridged as a conference, which is the only shape that can be
   * put on hold. Known from the create response, before the first poll.
   */
  conference: boolean;
  /** The customer is on hold. Server state - there is no SDK event for it. */
  hold: boolean;
  /** A hold request is in flight. Unlike mute, this one is a network round trip. */
  holdPending: boolean;
  /**
   * The two of them can actually hear each other.
   *
   * NOT the same as answered. `answerOnBridge` has no conference equivalent, so
   * `accept` now fires when the browser joins an empty room, and `answeredAt`
   * on the server is written when the customer's phone is PICKED UP - several
   * seconds before the announcement finishes and they enter the room. This
   * comes from the customer's own `participant-join`.
   */
  bridged: boolean;
  /**
   * How the line is carrying right now: "ok", "degraded", or "lost".
   *
   * NOT `error`, and the distinction is the whole point. `error` is the modal's
   * picker - it says the call is over and why. A bad line is not over: the two
   * of them may well still be talking through it, the phase stays `active`, and
   * every control stays live. This is a quality reading beside a call that is
   * still running, so it gets its own field and its own quiet strip.
   *
   * Derived in `linkQuality.ts` from two SDK inputs that must not be merged too
   * early - see the file for why a boolean here would look right and be wrong.
   */
  link: CallLink;
  /**
   * The third party on this call, or null when there is nobody.
   *
   * Server state with no SDK equivalent, exactly like hold: their phone rings
   * somewhere else entirely and the browser cannot see any of it. Note the
   * `mode`, which changes what the red button means - see `LiveCall`.
   */
  transfer: CallTransferState | null;
  /** Why nobody can be invited right now, or "" when somebody can. */
  transferBlocked: TransferBlock;
  /** A transfer request is in flight. */
  transferPending: boolean;
  /** The last transfer attempt's own refusal, shown beside the control. */
  transferError: string | null;
  /**
   * The operator is on this call from their own mobile, so there is no `Call`
   * object behind the window.
   *
   * Read by three things and each of them would be wrong without it: the
   * controls row, which takes mute, the keypad and the audio routing away
   * because all three are operations on that missing object; the unload
   * warning, which must not claim that closing the tab ends a conversation
   * happening on a telephone; and the live view, which has no local audio to
   * meter. Hold and transfer are untouched - both are server-side operations on
   * a conference participant and work exactly as they do in the browser.
   *
   * Set from the CREATE RESPONSE rather than from what the pad asked for. The
   * server re-checks the bridge mode and the saved number, so "I asked for the
   * mobile" and "the call is on the mobile" are two different claims.
   */
  viaMobile: boolean;
  /**
   * The ROW's own status, verbatim from the poll, or "" before the first tick.
   *
   * Added for one question the browser cannot otherwise answer on a mobile
   * call: WHOSE PHONE IS RINGING. The operator's own mobile is rung first and
   * the customer only after they pick up, so the whole of `phase: "ringing"`
   * covers two very different moments - and telling an operator "ringing" while
   * the thing ringing is in their pocket is how this feature would fail in
   * practice. The row's status separates them: it is written by the CUSTOMER
   * leg's callbacks, so it does not reach "ringing" until their phone does.
   *
   * Deliberately not folded into `phase`. That is the browser's own state
   * machine and every reader of it would have to learn a new value; this is one
   * more fact from the server, sitting beside `hold` and `bridged`, which are
   * the same kind of thing.
   */
  serverStatus: string;
  /**
   * This window is taking a live AI call over (ring R6d), rather than placing or
   * answering a call of its own.
   *
   * The call already exists: the AI is talking to the callee, and this browser's
   * leg is on its way into the takeover room. So several things a placed call
   * does are wrong here - the red button before the bridge calls the takeover
   * off instead of hanging up on the callee, there is no ringback and no redial,
   * hold and transfer are not offered (the row is not a conference bridge), and
   * the co-pilot stays out of it. `conference` stays false for the same reason.
   */
  takeover: boolean;
}

/** No call, nothing offered, nothing left over from the last one. */
export const IDLE: CallState = {
  phase: "idle",
  direction: "outbound",
  target: null,
  incoming: null,
  dialedNumber: null,
  callLogId: null,
  error: null,
  muted: false,
  recording: false,
  // "not_live" until the first poll answers, for the same reason
  // `transferBlocked` starts blocked: a call that has not been answered yet has
  // no recording running, and a button that refuses its first click is worse
  // than one that is plainly not available yet.
  recordingStopBlocked: "not_live",
  recordingStopPending: false,
  recordingStopped: null,
  recordingStopError: null,
  answeredAt: null,
  finalDuration: null,
  conference: false,
  hold: false,
  holdPending: false,
  bridged: false,
  link: "ok",
  transfer: null,
  // "not_bridged" rather than "" until the first poll answers: a call that has
  // not connected yet genuinely has nobody to invite anybody onto, and an
  // enabled control that refuses the first click is worse than a disabled one.
  transferBlocked: "not_bridged",
  transferPending: false,
  transferError: null,
  viaMobile: false,
  serverStatus: "",
  takeover: false,
};
